import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  getOccupancyNotifications, 
  processOccupancyNotification,
  getTenants,
  getTenantLeases,
  getRentPayments,
  generateMonthlyRent,
  removeTenant,
  recordTenantPayment,
  getSystemKeys,
  getPendingUsers,
  approveUser,
  rejectUser,
  approveTenantRemoval,
  rejectTenantRemoval,
  approveTenantVacancy,
  rejectTenantVacancy,
  getOutstandingDuesRemovedUsers
} from '@/services/api';
import { 
  Users, 
  IndianRupee, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Home,
  UserPlus,
  UserMinus,
  UserX,
  FileText,
  Mail,
  Phone,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Trash2,
  CreditCard,
  Copy,
  Calendar
} from 'lucide-react';

const AdminOccupancy = () => {
  const [notifications, setNotifications] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [rentPayments, setRentPayments] = useState([]);
  const [systemKeys, setSystemKeys] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [outstandingDues, setOutstandingDues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Dialog states
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [approvedCredentials, setApprovedCredentials] = useState(null);
  
  // User approval dialog states
  const [showUserRejectDialog, setShowUserRejectDialog] = useState(false);
  const [selectedPendingUser, setSelectedPendingUser] = useState(null);
  const [userRejectionReason, setUserRejectionReason] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [notifsData, tenantsData, leasesData, paymentsData, keysData, pendingData, duesData] = await Promise.all([
        getOccupancyNotifications(),
        getTenants(),
        getTenantLeases(),
        getRentPayments(),
        getSystemKeys().catch(() => null),
        getPendingUsers().catch(() => []),
        getOutstandingDuesRemovedUsers().catch(() => null)
      ]);
      setNotifications(notifsData);
      setTenants(tenantsData);
      setLeases(leasesData);
      setRentPayments(paymentsData);
      setSystemKeys(keysData);
      setPendingUsers(pendingData);
      setOutstandingDues(duesData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedNotif) return;
    try {
      setProcessing(selectedNotif.id);
      const result = await processOccupancyNotification(selectedNotif.id, 'approved', remarks);
      toast.success('Request approved successfully!');
      setShowApproveDialog(false);
      
      // Show credentials if tenant was created
      if (result.tenant_credentials) {
        setApprovedCredentials(result.tenant_credentials);
        setShowCredentialsDialog(true);
      }
      
      setSelectedNotif(null);
      setRemarks('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!selectedNotif) return;
    try {
      setProcessing(selectedNotif.id);
      await processOccupancyNotification(selectedNotif.id, 'rejected', remarks);
      toast.success('Request rejected');
      setShowRejectDialog(false);
      setSelectedNotif(null);
      setRemarks('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  const handleGenerateMonthlyRent = async () => {
    try {
      setGenerating(true);
      const result = await generateMonthlyRent();
      if (result.created_count > 0) {
        toast.success(result.message);
      } else {
        toast.info(result.message);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate monthly rent');
    } finally {
      setGenerating(false);
    }
  };

  const handleRemoveTenant = async () => {
    if (!selectedTenant) return;
    try {
      setProcessing(selectedTenant.id);
      await removeTenant(selectedTenant.id);
      toast.success('Tenant removed successfully');
      setShowRemoveDialog(false);
      setSelectedTenant(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove tenant');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveRemoval = async (tenantId) => {
    try {
      setProcessing(tenantId);
      await approveTenantRemoval(tenantId);
      toast.success('Tenant removal approved');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve removal');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectRemoval = async (tenantId) => {
    try {
      setProcessing(tenantId);
      await rejectTenantRemoval(tenantId);
      toast.success('Removal rejected - tenant restored to active');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject removal');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveVacancy = async (tenantId) => {
    try {
      setProcessing(tenantId);
      await approveTenantVacancy(tenantId);
      toast.success('Tenant vacancy approved - tenant has been removed');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve vacancy');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectVacancy = async (tenantId) => {
    try {
      setProcessing(tenantId);
      await rejectTenantVacancy(tenantId);
      toast.success('Vacancy rejected - tenant restored to active');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject vacancy');
    } finally {
      setProcessing(null);
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedTenant || !paymentAmount) return;
    try {
      setProcessing(selectedTenant.id);
      await recordTenantPayment(selectedTenant.id, parseFloat(paymentAmount));
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      setSelectedTenant(null);
      setPaymentAmount('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setProcessing(null);
    }
  };

  // Approve pending user registration (final approval)
  const handleApproveUser = async (userId) => {
    try {
      setProcessing(userId);
      await approveUser(userId);
      toast.success('User approved and activated!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    } finally {
      setProcessing(null);
    }
  };

  // Reject pending user registration
  const handleRejectUser = async () => {
    if (!selectedPendingUser) return;
    if (!userRejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      setProcessing(selectedPendingUser.id);
      await rejectUser(selectedPendingUser.id, userRejectionReason);
      toast.success('User registration rejected');
      setShowUserRejectDialog(false);
      setSelectedPendingUser(null);
      setUserRejectionReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    } finally {
      setProcessing(null);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        toast.success('Copied to clipboard');
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy. Please copy manually: ' + text);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      paid: 'bg-green-100 text-green-800 border-green-200',
      active: 'bg-blue-100 text-blue-800 border-blue-200',
      ended: 'bg-slate-100 text-slate-800 border-slate-200'
    };
    return variants[status] || variants.pending;
  };

  const pendingNotifications = notifications.filter(n => n.status === 'pending');
  const processedNotifications = notifications.filter(n => n.status !== 'pending');
  const activeLeases = leases.filter(l => l.status === 'active');
  const activeTenants = tenants.filter(t => t.role === 'tenant');
  const totalCollected = rentPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
  const totalPending = rentPayments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-occupancy-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Occupancy & Tenants</h1>
          <p className="text-slate-600 mt-1">Manage tenant approvals, leases, and rent</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleGenerateMonthlyRent} disabled={generating} data-testid="generate-rent-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Generating...' : 'Generate Monthly Rent'}
          </Button>
        </div>
      </div>

      {/* Pending Alert */}
      {pendingNotifications.length > 0 && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <AlertTitle className="text-amber-800">Action Required</AlertTitle>
          <AlertDescription className="text-amber-700">
            {pendingNotifications.length} pending request(s) need your attention.
          </AlertDescription>
        </Alert>
      )}

      {/* User Approvals Alert */}
      {pendingUsers.length > 0 && (
        <Alert className="border-green-300 bg-green-50">
          <UserPlus className="h-5 w-5 text-green-600" />
          <AlertTitle className="text-green-800">New User Registrations</AlertTitle>
          <AlertDescription className="text-green-700">
            {pendingUsers.length} user(s) waiting for final approval. Go to "User Approvals" tab.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className={pendingNotifications.length > 0 ? 'border-amber-300 bg-amber-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Pending</p>
                <p className="text-2xl font-bold text-slate-900">{pendingNotifications.length}</p>
              </div>
              <AlertCircle className={`h-8 w-8 ${pendingNotifications.length > 0 ? 'text-amber-500' : 'text-slate-300'}`} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Active Tenants</p>
                <p className="text-2xl font-bold text-slate-900">{activeTenants.length}</p>
              </div>
              <Users className="h-8 w-8 text-secondary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Active Leases</p>
                <p className="text-2xl font-bold text-slate-900">{activeLeases.length}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Collected</p>
                <p className="text-xl font-bold text-green-700 flex items-center gap-1">
                  <IndianRupee className="h-4 w-4" />
                  {totalCollected.toLocaleString('en-IN')}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Pending Rent</p>
                <p className="text-xl font-bold text-amber-700 flex items-center gap-1">
                  <IndianRupee className="h-4 w-4" />
                  {totalPending.toLocaleString('en-IN')}
                </p>
              </div>
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue={pendingUsers.length > 0 ? "approvals" : "pending"} className="space-y-4">
        <TabsList>
          <TabsTrigger value="approvals" data-testid="approvals-tab" className={pendingUsers.length > 0 ? "text-green-600" : ""}>
            <UserPlus className="h-4 w-4 mr-2" />
            User Approvals {pendingUsers.length > 0 && `(${pendingUsers.length})`}
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="pending-tab">
            Pending ({pendingNotifications.length})
          </TabsTrigger>
          <TabsTrigger value="tenants" data-testid="tenants-tab">Tenants</TabsTrigger>
          <TabsTrigger value="leases" data-testid="leases-tab">Leases</TabsTrigger>
          <TabsTrigger value="payments" data-testid="payments-tab">Payments</TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">History</TabsTrigger>
        </TabsList>

        {/* User Approvals Tab */}
        <TabsContent value="approvals">
          <Card>
            <CardHeader>
              <CardTitle>Pending User Approvals</CardTitle>
              <CardDescription>Final approval for new user registrations (Tenants, Residents, Security)</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-300" />
                  <p className="font-medium">No pending approvals</p>
                  <p className="text-sm">New user registrations will appear here for final approval</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingUsers.map((user) => (
                    <div 
                      key={user.id} 
                      className="p-4 bg-green-50 rounded-xl border border-green-200"
                      data-testid={`pending-user-${user.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <UserPlus className="h-6 w-6 text-green-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{user.name}</p>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                              <Badge className="bg-blue-100 text-blue-800 border-blue-200 capitalize">
                                {user.role?.replace('_', ' ') || 'tenant'}
                              </Badge>
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {user.email}
                              </span>
                              {user.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {user.phone}
                                </span>
                              )}
                              {user.unit_number && (
                                <span className="flex items-center gap-1">
                                  <Home className="h-3 w-3" />
                                  Unit {user.unit_number}
                                </span>
                              )}
                            </div>
                            {user.monthly_rent && (
                              <p className="text-sm text-slate-600 mt-1">
                                Monthly Rent: <span className="font-medium text-secondary">₹{Number(user.monthly_rent).toLocaleString('en-IN')}</span>
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              Status: <span className="font-medium capitalize">{user.status?.replace(/_/g, ' ') || 'Pending Admin Approval'}</span>
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-16 md:ml-0">
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                            Awaiting Final Approval
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => {
                              setSelectedPendingUser(user);
                              setShowUserRejectDialog(true);
                            }}
                            disabled={processing === user.id}
                            data-testid={`reject-user-${user.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApproveUser(user.id)}
                            disabled={processing === user.id}
                            data-testid={`approve-user-${user.id}`}
                          >
                            {processing === user.id ? (
                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                            )}
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending Requests</CardTitle>
              <CardDescription>Approve or reject tenant and occupancy requests</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingNotifications.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-300" />
                  <p className="font-medium">All caught up!</p>
                  <p className="text-sm">No pending requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingNotifications.map((notif) => (
                    <div key={notif.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid={`pending-${notif.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${notif.notification_type.includes('occupy') ? 'bg-green-100' : 'bg-red-100'}`}>
                            {notif.notification_type.includes('occupy') ? (
                              <UserPlus className="h-5 w-5 text-green-600" />
                            ) : (
                              <UserMinus className="h-5 w-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900 capitalize">
                              {notif.notification_type.replace(/_/g, ' ')}
                            </p>
                            <p className="text-sm text-slate-600">
                              From: {notif.user_name} | Unit: {notif.unit_number}
                            </p>
                          </div>
                        </div>
                        <Badge className={getStatusBadge('pending')}>Pending</Badge>
                      </div>

                      {notif.tenant_details && (
                        <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200">
                          <p className="text-sm font-medium text-slate-700 mb-2">Tenant Details:</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-slate-500">Name</p>
                              <p className="font-medium">{notif.tenant_details.name}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Email</p>
                              <p className="font-medium">{notif.tenant_details.email}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Phone</p>
                              <p className="font-medium">{notif.tenant_details.phone}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Monthly Rent</p>
                              <p className="font-medium flex items-center gap-1">
                                <IndianRupee className="h-3 w-3" />
                                {notif.tenant_details.monthly_rent?.toLocaleString('en-IN')}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-400">
                          {new Date(notif.created_at).toLocaleString('en-IN')}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => { setSelectedNotif(notif); setShowRejectDialog(true); }}
                            data-testid={`reject-${notif.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => { setSelectedNotif(notif); setShowApproveDialog(true); }}
                            data-testid={`approve-${notif.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tenants Tab */}
        <TabsContent value="tenants">
          <Card>
            <CardHeader>
              <CardTitle>All Tenants</CardTitle>
              <CardDescription>Manage all tenants in the society</CardDescription>
            </CardHeader>
            <CardContent>
              {tenants.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No tenants</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tenants.map((tenant) => {
                    const lease = leases.find(l => l.tenant_id === tenant.id && l.status === 'active');
                    const isPendingRemoval = tenant.status === 'pending_removal';
                    const isPendingVacancy = tenant.status === 'pending_vacancy';
                    const isPendingAdmin = tenant.status === 'pending_admin';
                    const hasPendingAction = isPendingRemoval || isPendingVacancy || isPendingAdmin;
                    
                    return (
                      <div key={tenant.id} className={`flex flex-col md:flex-row md:items-center justify-between p-4 rounded-xl ${isPendingRemoval ? 'bg-amber-50 border border-amber-200' : isPendingVacancy ? 'bg-orange-50 border border-orange-200' : isPendingAdmin ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50'}`} data-testid={`tenant-${tenant.id}`}>
                        <div className="flex items-center gap-4 mb-3 md:mb-0">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isPendingRemoval ? 'bg-amber-100' : isPendingVacancy ? 'bg-orange-100' : isPendingAdmin ? 'bg-blue-100' : 'bg-secondary/10'}`}>
                            <Users className={`h-5 w-5 ${isPendingRemoval ? 'text-amber-600' : isPendingVacancy ? 'text-orange-600' : isPendingAdmin ? 'text-blue-600' : 'text-secondary'}`} />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{tenant.name}</p>
                            <div className="flex items-center gap-3 text-sm text-slate-500 flex-wrap">
                              <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{tenant.email}</span>
                              {tenant.phone && (
                                <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{tenant.phone}</span>
                              )}
                              <span className="flex items-center gap-1"><Home className="h-3 w-3" />Unit {tenant.unit_number}</span>
                              {(tenant.monthly_rent || lease?.monthly_rent) && (
                                <span className="flex items-center gap-1"><IndianRupee className="h-3 w-3" />Rent: {(tenant.monthly_rent || lease?.monthly_rent)?.toLocaleString('en-IN')}/mo</span>
                              )}
                            </div>
                            {isPendingRemoval && (
                              <div className="mt-2 text-sm space-y-1">
                                <p className="text-amber-700 font-medium">Removal requested by: {tenant.removal_requested_by_name}</p>
                                <p className="text-amber-600">Reason: {tenant.removal_reason || 'Not specified'}</p>
                              </div>
                            )}
                            {isPendingVacancy && (
                              <div className="mt-2 text-sm">
                                <p className="text-orange-700 font-medium">Vacancy requested by: {tenant.vacancy_requested_by_name}</p>
                                <p className="text-orange-600">Reason: {tenant.vacancy_reason || 'Not specified'}</p>
                              </div>
                            )}
                            {isPendingAdmin && (
                              <div className="mt-2 text-sm">
                                <p className="text-blue-700 font-medium">Awaiting your approval to activate this tenant</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {isPendingAdmin ? (
                            <>
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                Pending Admin Approval
                              </Badge>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApproveUser(tenant.id)}
                                disabled={processing === tenant.id}
                                data-testid={`approve-tenant-${tenant.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                  setSelectedPendingUser(tenant);
                                  setShowUserRejectDialog(true);
                                }}
                                disabled={processing === tenant.id}
                                data-testid={`reject-tenant-${tenant.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          ) : isPendingRemoval ? (
                            <>
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                Pending Removal Approval
                              </Badge>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApproveRemoval(tenant.id)}
                                disabled={processing === tenant.id}
                                data-testid={`approve-removal-${tenant.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Approve Removal
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-slate-600"
                                onClick={() => handleRejectRemoval(tenant.id)}
                                disabled={processing === tenant.id}
                                data-testid={`reject-removal-${tenant.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          ) : isPendingVacancy ? (
                            <>
                              <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                                Pending Vacancy Approval
                              </Badge>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApproveVacancy(tenant.id)}
                                disabled={processing === tenant.id}
                                data-testid={`approve-vacancy-${tenant.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Approve Vacancy
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-slate-600"
                                onClick={() => handleRejectVacancy(tenant.id)}
                                disabled={processing === tenant.id}
                                data-testid={`reject-vacancy-${tenant.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          ) : (
                            <>
                              <Badge className={getStatusBadge(tenant.role === 'tenant' ? 'active' : 'ended')}>
                                {tenant.role === 'tenant' ? 'Active' : 'Inactive'}
                              </Badge>
                              {tenant.role === 'tenant' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedTenant(tenant);
                                      setPaymentAmount(lease?.monthly_rent?.toString() || '');
                                      setShowPaymentDialog(true);
                                    }}
                                    data-testid={`record-payment-${tenant.id}`}
                                  >
                                    <CreditCard className="h-4 w-4 mr-1" />
                                    Record Payment
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => { setSelectedTenant(tenant); setShowRemoveDialog(true); }}
                                    data-testid={`remove-${tenant.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leases Tab */}
        <TabsContent value="leases">
          <Card>
            <CardHeader>
              <CardTitle>Lease Agreements</CardTitle>
              <CardDescription>All lease agreements</CardDescription>
            </CardHeader>
            <CardContent>
              {leases.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No leases</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {leases.map((lease) => (
                    <div key={lease.id} className="p-4 bg-slate-50 rounded-xl" data-testid={`lease-${lease.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold text-slate-900">{lease.tenant_name}</p>
                          <p className="text-sm text-slate-500">Landlord: {lease.landlord_name}</p>
                        </div>
                        <Badge className={getStatusBadge(lease.status)}>{lease.status}</Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div><p className="text-slate-500">Unit</p><p className="font-medium">{lease.unit_number}</p></div>
                        <div><p className="text-slate-500">Rent</p><p className="font-medium">₹{lease.monthly_rent?.toLocaleString('en-IN')}</p></div>
                        <div><p className="text-slate-500">Start</p><p className="font-medium">{lease.lease_start}</p></div>
                        <div><p className="text-slate-500">End</p><p className="font-medium">{lease.lease_end || 'Ongoing'}</p></div>
                        <div><p className="text-slate-500">Email</p><p className="font-medium text-xs">{lease.tenant_email}</p></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle>Rent Payments</CardTitle>
              <CardDescription>All rent payment records</CardDescription>
            </CardHeader>
            <CardContent>
              {rentPayments.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <IndianRupee className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No payments yet</p>
                  <p className="text-sm">Click "Generate Monthly Rent" to create entries</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rentPayments.map((payment) => (
                    <div key={payment.id} className={`flex items-center justify-between p-4 rounded-xl ${payment.status === 'paid' ? 'bg-green-50' : payment.status === 'submitted' ? 'bg-blue-50' : 'bg-slate-50'}`} data-testid={`payment-${payment.id}`}>
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${payment.status === 'paid' ? 'bg-green-100' : payment.status === 'submitted' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                          {payment.status === 'paid' ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : payment.status === 'submitted' ? <Clock className="h-5 w-5 text-blue-600" /> : <Clock className="h-5 w-5 text-amber-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{payment.tenant_name}</p>
                          <p className="text-sm text-slate-500">{payment.month ? formatMonth(payment.month) : 'N/A'} | Unit {payment.unit_number} | To: {payment.landlord_name}</p>
                          {payment.status === 'paid' && (payment.confirmed_by_name || payment.recorded_by_name) && (
                            <p className="text-xs text-green-600 mt-0.5">
                              {payment.confirmed_by_name
                                ? `Confirmed by ${payment.confirmed_by_name} on ${payment.confirmed_date}`
                                : `Recorded by ${payment.recorded_by_name} on ${payment.paid_date}`
                              }
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900 flex items-center gap-1 justify-end">
                          <IndianRupee className="h-4 w-4" />{payment.amount?.toLocaleString('en-IN')}
                        </p>
                        <Badge className={getStatusBadge(payment.status)}>
                          {payment.status === 'submitted' ? 'Submitted (Awaiting Confirmation)' : payment.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Outstanding Dues from Removed Tenants */}
          {outstandingDues && (outstandingDues.unpaid_rent?.length > 0 || outstandingDues.unpaid_bills?.length > 0) && (
            <Card className="border-red-300 bg-red-50/30 mt-4" data-testid="occupancy-outstanding-dues">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <UserX className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="text-red-900">Outstanding Dues — Removed Users</CardTitle>
                    <CardDescription className="text-red-700">
                      These users are no longer in the society but have pending amounts due
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 bg-red-100 border border-red-200 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-700" />
                    <span className="font-semibold text-red-900 text-sm">
                      {outstandingDues.summary.total_removed_users_with_dues} removed user(s)
                    </span>
                  </div>
                  <p className="font-bold text-red-900 flex items-center gap-1">
                    <IndianRupee className="h-4 w-4" />
                    {outstandingDues.summary.total_outstanding?.toLocaleString('en-IN')}
                  </p>
                </div>

                {outstandingDues.unpaid_rent?.map((rent, idx) => (
                  <div key={rent.id || idx} className="flex items-center justify-between p-4 bg-white rounded-xl border border-red-200" data-testid={`occupancy-removed-rent-${idx}`}>
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-lg bg-red-100">
                        <UserX className="h-5 w-5 text-red-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{rent.tenant_name}</p>
                        <p className="text-sm text-slate-500">Rent for {rent.month || 'N/A'} | Unit {rent.tenant_unit}</p>
                        <Badge className="bg-red-100 text-red-700 border-red-200 text-xs mt-1">No longer in society — amount is due</Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-800 flex items-center gap-1 justify-end">
                        <IndianRupee className="h-4 w-4" />{rent.amount?.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                ))}

                {outstandingDues.unpaid_bills?.map((bill) => (
                  <div key={bill.id} className="flex items-center justify-between p-4 bg-white rounded-xl border border-red-200" data-testid={`occupancy-removed-bill-${bill.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-lg bg-red-100">
                        <UserX className="h-5 w-5 text-red-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{bill.user_name}</p>
                        <p className="text-sm text-slate-500">{bill.description} | Unit {bill.user_unit}</p>
                        <Badge className="bg-red-100 text-red-700 border-red-200 text-xs mt-1">No longer in society — amount is due</Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-800 flex items-center gap-1 justify-end">
                        <IndianRupee className="h-4 w-4" />{bill.amount?.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Request History</CardTitle>
              <CardDescription>Approved or rejected tenant occupy, vacancy, and removal requests</CardDescription>
            </CardHeader>
            <CardContent>
              {processedNotifications.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No requests have been processed yet</p>
                  <p className="text-sm">Approved or rejected vacancy and removal requests will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {processedNotifications.map((notif) => (
                    <div key={notif.id} className="p-4 bg-slate-50 rounded-xl" data-testid={`history-${notif.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {notif.notification_type.includes('occupy') ? <UserPlus className="h-5 w-5 text-slate-500" /> : <UserMinus className="h-5 w-5 text-slate-500" />}
                          <span className="font-medium capitalize">{notif.notification_type.replace(/_/g, ' ')}</span>
                        </div>
                        <Badge className={getStatusBadge(notif.status)}>{notif.status}</Badge>
                      </div>
                      <p className="text-sm text-slate-600">From: {notif.user_name} | Unit: {notif.unit_number}</p>
                      {notif.tenant_details?.name && (
                        <p className="text-sm text-slate-600">Tenant: {notif.tenant_details.name}</p>
                      )}
                      {notif.reason && <p className="text-sm text-slate-500 mt-1">Reason: {notif.reason}</p>}
                      {notif.admin_remarks && <p className="text-sm text-slate-500 mt-2 bg-white p-2 rounded">Remarks: {notif.admin_remarks}</p>}
                      {notif.processed_at && (
                        <p className="text-xs text-slate-400 mt-2">
                          Processed: {new Date(notif.processed_at).toLocaleString('en-IN')}
                        </p>
                      )}
                      {notif.tenant_key && <p className="text-sm text-green-600 mt-1">Tenant Key: {notif.tenant_key}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Request</DialogTitle>
            <DialogDescription>
              {selectedNotif?.notification_type === 'tenant_occupy' 
                ? 'This will create a tenant account with login credentials.'
                : 'Confirm approval of this request.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="approve-remarks">Remarks (optional)</Label>
            <Input id="approve-remarks" placeholder="Add notes..." value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Cancel</Button>
            <Button onClick={handleApprove} disabled={processing} data-testid="confirm-approve">
              {processing ? 'Processing...' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Request</DialogTitle>
            <DialogDescription>Provide a reason for rejection.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reject-remarks">Reason</Label>
            <Input id="reject-remarks" placeholder="Enter reason..." value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={processing} data-testid="confirm-reject">
              {processing ? 'Processing...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tenant Account Created</DialogTitle>
            <DialogDescription>Share these credentials with the tenant.</DialogDescription>
          </DialogHeader>
          {approvedCredentials && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-green-700">Email:</span>
                  <div className="flex items-center gap-2">
                    <code className="bg-green-100 px-2 py-1 rounded">{approvedCredentials.email}</code>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(approvedCredentials.email)}><Copy className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-green-700">Password:</span>
                  <div className="flex items-center gap-2">
                    <code className="bg-green-100 px-2 py-1 rounded">{approvedCredentials.default_password}</code>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(approvedCredentials.default_password)}><Copy className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-green-700">Tenant Key:</span>
                  <div className="flex items-center gap-2">
                    <code className="bg-green-100 px-2 py-1 rounded font-bold">{approvedCredentials.tenant_key}</code>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(approvedCredentials.tenant_key)}><Copy className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-500">Tenant should change their password after first login.</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowCredentialsDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Tenant Dialog */}
      <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Tenant</DialogTitle>
            <DialogDescription>Remove {selectedTenant?.name} from the society?</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {selectedTenant && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                <p className="text-slate-700"><strong>Name:</strong> {selectedTenant.name}</p>
                <p className="text-slate-700"><strong>Email:</strong> {selectedTenant.email}</p>
                <p className="text-slate-700"><strong>Phone:</strong> {selectedTenant.phone || 'N/A'}</p>
                <p className="text-slate-700"><strong>Unit:</strong> {selectedTenant.unit_number || 'N/A'}</p>
                {(selectedTenant.monthly_rent || leases.find(l => l.tenant_id === selectedTenant.id)?.monthly_rent) && (
                  <p className="text-slate-700"><strong>Rent:</strong> {'\u20b9'}{(selectedTenant.monthly_rent || leases.find(l => l.tenant_id === selectedTenant.id)?.monthly_rent)?.toLocaleString('en-IN')}/mo</p>
                )}
              </div>
            )}
            <Alert variant="destructive">
              <AlertCircle className="h-5 w-5" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>This will end the lease and deactivate the account. This action cannot be undone.</AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemoveDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRemoveTenant} disabled={processing} data-testid="confirm-remove">
              {processing ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>Record rent payment for {selectedTenant?.name}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="amount">Amount (₹)</Label>
            <Input id="amount" type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder="15000" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={processing} data-testid="confirm-record-payment">
              {processing ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Reject Dialog */}
      <Dialog open={showUserRejectDialog} onOpenChange={(open) => { setShowUserRejectDialog(open); if (!open) setUserRejectionReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject User Registration</DialogTitle>
            <DialogDescription>Reject {selectedPendingUser?.name}'s registration?</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-5 w-5" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>This will permanently delete the user's registration. They will need to register again.</AlertDescription>
            </Alert>
            <div>
              <Label htmlFor="user-rejection-reason">Reason for Rejection *</Label>
              <textarea
                id="user-rejection-reason"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-2"
                placeholder="Please provide a reason for rejecting this registration..."
                value={userRejectionReason}
                onChange={(e) => setUserRejectionReason(e.target.value)}
                data-testid="user-rejection-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUserRejectDialog(false); setUserRejectionReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectUser} disabled={processing || !userRejectionReason.trim()} data-testid="confirm-reject-user">
              {processing ? 'Rejecting...' : 'Reject Registration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOccupancy;
